import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { fetchProductSubscription } from 'actions/LuxPlusActions'
import FormatCurrency from 'components/Common/FormatCurrency'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useAppDispatch } from 'hooks/reduxHooks'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import IconLoadingBox from 'LuxKit/Icons/IconLoadingBox'
import LuxPlusBenefitsListLoadingSkeleton from '../LuxPlusBenefitsListLoadingSkeleton'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LuxPlusSubscriptionCancellationModal from '../LuxPlusSubscriptionCancellationModal'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import offerPageURL from 'lib/offer/offerPageURL'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import LuxPlusBenefitsList from '../LuxPlusBenefitsList'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import GeoContext from 'contexts/geoContext'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineUsdCircleIcon from 'LuxKit/Icons/line/LineUsdCircleIcon'
import LuxPlusSubscriptionTsAndCs from '../LuxPlusSubscriptionTsAndCs'
import LineLockIcon from 'LuxKit/Icons/line/LineLockIcon'
import { connect } from 'react-redux'
import { isBedbankOffer, isLEOffer, isTourV2Offer, isCruiseOffer } from 'lib/offer/offerTypes'
import { formatInclusionsTitleLuxPlus } from 'constants/inclusions'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import useLuxPlusInsuranceAvailableCopy from 'checkout/hooks/useLuxPlusInsuranceAvailableCopy'
import useLuxPlusBookingProtectionAvailableCopy from 'checkout/hooks/useLuxPlusBookingProtectionAvailableCopy'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import useEnabledLuxPlusBenefits from 'luxPlus/hooks/useEnabledLuxPlusBenefits'
import orderLuxPlusBenefitsByPriority from 'luxPlus/lib/orderLuxPlusBenefitsByPriority'
import { earlyAccessRestrictedToMembers } from 'luxPlus/lib/utils'

type InheritedModalProps = Pick<React.ComponentProps<typeof Modal>,
  'onPrimaryActionClick' |
  'primaryActionText' |
  'isSecondaryActionHidden' |
  'isPrimaryActionHidden' |
  'secondaryActionText' |
  'onSecondaryActionClick'
>

interface MappedProps {
  subscription: App.ProductSubscription
}

interface Props extends MappedProps, InheritedModalProps {
  title?: string
  subTitle?: React.ReactNode
  offer?: App.Offer | App.OfferSummary | App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary | App.CruiseOffer | App.BedbankOffer | App.BedbankOfferSummary
  saveAmount?: number
  type?: App.LuxPlusLabelModalType
  hideSaveAmountPlus?: boolean
  providerPage?: App.LuxPlusLabelPage
  inclusions?: Array<App.PackageInclusion>
  tileInclusions?: Array<App.OfferInclusion>
  hideSpecialLabel?: boolean
  /** List these benefit types first in the “unlimited benefits” section (others keep API order). */
  priorityBenefitTypes?: Array<App.LuxPlusBenefitType>
}

interface Copy {
  title: string
  subTitle: React.ReactNode
  firstBenefit?: string
  saveAmountBenefit?: string
  insuranceBenefit?: string
}

function LuxPlusBenefitsModal(props: Props) {
  const {
    offer,
    saveAmount,
    type,
    subscription,
    hideSaveAmountPlus,
    providerPage,
    inclusions,
    tileInclusions,
    hideSpecialLabel = false,
    priorityBenefitTypes,
  } = props
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const { resolve } = useModalElementContext()
  const isLuxPlusLandingPage = providerPage === 'landing-page'
  const isOfferPage = providerPage === 'offer-page'

  const offerUrl = offerPageURL(offer)
  const geo = useContext(GeoContext)
  const formattedSaveAmount = formatCurrency(saveAmount ?? 0, geo.currentCurrency, geo.currentRegionCode, { format: 'roundedDollar' })
  const saveAmountSuffix = hideSaveAmountPlus ? '' : '+'
  const insuranceAvailableCopy = useLuxPlusInsuranceAvailableCopy()
  const bookingProtectionCopy = useLuxPlusBookingProtectionAvailableCopy()
  const enabledBenefits = useEnabledLuxPlusBenefits(subscription.offers?.[0].benefits)
  const benefitsForList = useMemo(
    () => orderLuxPlusBenefitsByPriority(enabledBenefits, priorityBenefitTypes),
    [enabledBenefits, priorityBenefitTypes],
  )
  const isEarlyAccessRestrictedToMembers = earlyAccessRestrictedToMembers(offer)

  const onShowCancellationPolicy = useCallback(() => {
    showModal(<LuxPlusSubscriptionCancellationModal offer={subscription.offers?.[0]} />)
  }, [showModal, subscription.offers])

  useEffect(() => {
    dispatch(fetchProductSubscription())
  }, [dispatch])

  const thisHotelLabel = intl.formatMessage({
    id: 'u1gjulOA2b2Va9IZfe2c',
    defaultMessage: 'this hotel',
    description: 'Generic fallback for hotel name in LuxPlus+ benefits modal subtitle',
  })
  const thisTourLabel = intl.formatMessage({
    id: 'y0InmSD5RP6Xt-3jrCSC',
    defaultMessage: 'this tour',
    description: 'Generic fallback for tour name in LuxPlus+ benefits modal subtitle',
  })
  const thisCruiseLabel = intl.formatMessage({
    id: '41oksZXZREXtS-a4aKX8',
    defaultMessage: 'this cruise',
    description: 'Generic fallback for cruise name in LuxPlus+ benefits modal subtitle',
  })

  const copy = useMemo<Copy>(() => {
    switch (type) {
      case 'early-access-offer':
        return {
          title: intl.formatMessage({
            id: '2mkq3ZbOctfzmmGHGJ-f',
            defaultMessage: 'Join {programName} to get early access to this offer and more!',
            description: 'Benefits modal title for early access offers on the offer page',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME }),
          subTitle: <FormattedMessage
            id="XS06FMY_RYFeEp1QUrCI"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isLEOffer(offer) && !isEarlyAccessRestrictedToMembers ? <b>{offer?.property?.name}</b> : thisHotelLabel,
            }}
          />,
          firstBenefit: intl.formatMessage({
            id: '0zDzBicVEM1eskuG_LMt',
            defaultMessage: 'Secure your room and dates before the general public',
            description: 'Benefit text about early access to rooms and dates before non-members',
          }),
          saveAmountBenefit: intl.formatMessage({
            id: 'Nm2DjrBRXGOgYpGHkT3f',
            defaultMessage: 'Save {saveAmount} on this booking with member pricing',
            description: 'Benefit text showing savings amount with member pricing on current booking',
          }, { saveAmount: formattedSaveAmount + saveAmountSuffix }),
        }
      case 'member-price-offer':
        return {
          title: intl.formatMessage({
            id: 'ZJVPdqrh3TMH8h6UnDvZ',
            defaultMessage: 'Join {programName} to save {saveAmount} on this offer and more!',
            description: 'Benefits modal title showing savings amount for member pricing offers',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME, saveAmount: formattedSaveAmount + saveAmountSuffix }),
          subTitle: <FormattedMessage
            id="XS06FMY_RYFeEp1QUrCI"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isLEOffer(offer) ? <b>{offer?.property?.name}</b> : thisHotelLabel,
            }}
          />,
          saveAmountBenefit: intl.formatMessage({
            id: 'anVYJj0HDiT7IpQU7E4l',
            defaultMessage: 'Save {saveAmount} on this booking',
            description: 'Benefit text showing savings amount on current booking',
          }, { saveAmount: formattedSaveAmount + saveAmountSuffix }),
        }
      case 'member-price-offer-cruise':
        return {
          title: intl.formatMessage({
            id: 'F6rnrvlEM7QqoCX-_3Si',
            defaultMessage: 'Join {programName} first to unlock discounts on this cruise and more!',
            description: 'Benefits modal title for cruise offers with LuxPlus discounts available but no specific save amount shown',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME }),
          subTitle: <FormattedMessage
            id="XS06FMY_RYFeEp1QUrCI"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isCruiseOffer(offer) ? <b>{offer?.name}</b> : thisCruiseLabel,
            }}
          />,
        }
      case 'member-price-offer-lpp':
        return {
          title: intl.formatMessage({
            id: 'ZJVPdqrh3TMH8h6UnDvZ',
            defaultMessage: 'Join {programName} to save {saveAmount} on this offer and more!',
            description: 'Benefits modal title showing savings amount for member pricing offers',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME, saveAmount: formattedSaveAmount + saveAmountSuffix }),
          subTitle: <FormattedMessage
            id="XS06FMY_RYFeEp1QUrCI"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isBedbankOffer(offer) ? <b>{offer?.name}</b> : thisHotelLabel,
            }}
          />,
          saveAmountBenefit: intl.formatMessage({
            id: 'anVYJj0HDiT7IpQU7E4l',
            defaultMessage: 'Save {saveAmount} on this booking',
            description: 'Benefit text showing savings amount on current booking',
          }, { saveAmount: formattedSaveAmount + saveAmountSuffix }),
        }
      case 'member-price-offer-lpp-no-save-amount':
        return {
          title: intl.formatMessage({
            id: '5_t-Et7h-wsrDd4bSm6t',
            defaultMessage: '{programName} discounts available on this offer and more!',
            description: 'Benefits modal title when no specific save amount shown for bedbank offers',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME }),
          subTitle: <FormattedMessage
            id="XS06FMY_RYFeEp1QUrCI"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isBedbankOffer(offer) ? <b>{offer?.name}</b> : thisHotelLabel,
            }}
          />,
        }
      case 'bonus-inclusions':
      case 'member-inclusions':
        return {
          title: intl.formatMessage({
            id: 'xbuzzbQSIyMZD0rdAqb0',
            defaultMessage: 'Join {programName} to enjoy {inclusionsTitle} for this offer and more!',
            description: 'Benefits modal title for bonus inclusions offers in a modal dialog',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME, inclusionsTitle: formatInclusionsTitleLuxPlus(intl) }),
          subTitle: <FormattedMessage
            id="SIUsOjgftjvMqZo4GY8U"
            defaultMessage="Become a member of our VIP travel club for {price} per year to unlock {inclusionsTitle} when you book {offerName}."
            description="Subtitle encouraging user to join VIP travel club to unlock bonus inclusions for a specific offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              inclusionsTitle: formatInclusionsTitleLuxPlus(intl),
              offerName: isLEOffer(offer) ? <b>{offer?.property?.name}</b> : isCruiseOffer(offer) ? thisCruiseLabel : thisHotelLabel,
            }}
          />,
        }
      case 'member-price-offer-tour':
        return {
          title: intl.formatMessage({
            id: 'E94k4pBvu0HCmWLKikZd',
            defaultMessage: 'Join {programName} to save {saveAmount}/person on this offer and more!',
            description: 'Benefits modal title showing per-person savings for tour offers',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME, saveAmount: formattedSaveAmount + saveAmountSuffix }),
          subTitle: <FormattedMessage
            id="XS06FMY_RYFeEp1QUrCI"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isTourV2Offer(offer) ? <b>{offer?.name}</b> : thisTourLabel,
            }}
          />,
          saveAmountBenefit: intl.formatMessage({
            id: 'tAmfBjrT4fWGM1RpW-hx',
            defaultMessage: 'Save {saveAmount}/person on this booking',
            description: 'Benefit text showing per-person savings on current tour booking',
          }, { saveAmount: formattedSaveAmount + saveAmountSuffix }),
        }
      case 'options-available-tour':
        return {
          title: intl.formatMessage({
            id: 'jIcg1tCJ8sgLCvxCBfql',
            defaultMessage: 'Join {programName} to save on select tour options on this offer and more!',
            description: 'Benefits modal title for tours with LuxPlus+ pricing options available',
          }, { programName: LUXURY_PLUS.PROGRAM_NAME }),
          subTitle: <FormattedMessage
            id="XS06FMY_RYFeEp1QUrCI"
            defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock instant benefits when you book {offerName} today. Cancel anytime!"
            description="Subtitle encouraging user to join VIP travel club at a yearly price to unlock benefits when booking an offer"
            values={{
              price: <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="roundedDollar" />,
              offerName: isTourV2Offer(offer) ? <b>{offer?.name}</b> : thisTourLabel,
            }}
          />,
        }
      default:
        return {
          title: props.title ?? '',
          subTitle: props.subTitle ?? '',
          saveAmountBenefit: formattedSaveAmount ? intl.formatMessage({
            id: 'Nm2DjrBRXGOgYpGHkT3f',
            defaultMessage: 'Save {saveAmount} on this booking with member pricing',
            description: 'Benefit text showing savings amount with member pricing on current booking',
          }, { saveAmount: formattedSaveAmount + saveAmountSuffix }) : '',
        }
    }
  }, [formattedSaveAmount, intl, offer, props.subTitle, props.title, saveAmountSuffix, subscription.offers, type, isEarlyAccessRestrictedToMembers, thisHotelLabel, thisTourLabel, thisCruiseLabel])

  const viewOfferLabel = intl.formatMessage({
    id: 'j7U3vA19qG3UYx9wU98q',
    defaultMessage: 'View offer',
    description: 'Primary action button text to view the offer in LuxPlus+ benefits modal',
  })
  const learnMoreLabel = intl.formatMessage({
    id: '8EKWPyeHNLCuXHvwPVmT',
    defaultMessage: 'Learn more about {programName}',
    description: 'Secondary action button text to learn more about LuxPlus+ membership',
  }, { programName: LUXURY_PLUS.PROGRAM_NAME })

  const defaultSecondaryTo = `/${LUXURY_PLUS_PATHS.LANDING_PAGE}`
  const hasCustomSecondaryNavigation = Boolean(props.onSecondaryActionClick)
  const resolvedSecondaryTo = hasCustomSecondaryNavigation
    ? undefined
    : defaultSecondaryTo
  const resolvedSecondaryText = props.secondaryActionText ?? learnMoreLabel

  return <Modal
    primaryActionText={props.primaryActionText ?? viewOfferLabel}
    isPrimaryActionHidden={props.isPrimaryActionHidden || !offerUrl || isOfferPage}
    primaryActionTo={props.onPrimaryActionClick ? undefined : offerUrl}
    isSecondaryActionHidden={props.isSecondaryActionHidden ?? isLuxPlusLandingPage}
    secondaryActionText={resolvedSecondaryText}
    secondaryActionTo={resolvedSecondaryTo}
    title={copy.title}
    onPrimaryActionClick={props.onPrimaryActionClick}
    onSecondaryActionClick={props.onSecondaryActionClick}
  >
    {subscription.error && <BodyText variant="medium">{intl.formatMessage({
      id: 'bNDEh6UVDFq6JUAgbffd',
      defaultMessage: 'There was an error loading the price details. Please try again later.',
      description: 'Error message when LuxPlus+ subscription pricing fails to load in benefits modal',
    })}
    </BodyText>}
    {subscription.fetching &&
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={12}>
          <BodyText variant="medium">
            <TextLoadingBox width="100%" />
            <TextLoadingBox width="40ch" />
          </BodyText>
          <Group direction="vertical" gap={8}>
            <BodyTextBlock variant="medium" startIcon={<IconLoadingBox colour="highlight-primary-dark" />}>
              <TextLoadingBox colour="highlight-primary-dark" width="16ch" />
            </BodyTextBlock>
            <BodyTextBlock variant="medium" startIcon={<IconLoadingBox colour="highlight-primary-dark" />}>
              <TextLoadingBox colour="highlight-primary-dark" width="24ch" />
            </BodyTextBlock>
          </Group>
          <BodyText variant="medium">
            <TextLoadingBox width="40ch" />
          </BodyText>
          <LuxPlusBenefitsListLoadingSkeleton benefitsCounts={6} />
          <BodyTextBlock variant="medium" endIcon={<IconLoadingBox />}>
            <TextLoadingBox colour="highlight-primary-dark" width="16ch" />
          </BodyTextBlock>
        </Group>
        <BodyText variant="small">
          <TextLoadingBox width="100%" />
          <TextLoadingBox width="24ch" />
        </BodyText>
      </Group>}
    {subscription.offers &&
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={12}>
          {copy.subTitle && <BodyText variant="medium">
            {copy.subTitle}
          </BodyText>}
          <Group direction="vertical" gap={8}>
            {copy.firstBenefit && <BodyTextBlock variant="medium" weight="bold" colour="highlight-primary-dark" startIcon={<LineLockIcon />}>
              {copy.firstBenefit}
            </BodyTextBlock>}
            {!!saveAmount && copy.saveAmountBenefit && <BodyTextBlock variant="medium" weight="bold" colour="highlight-primary-dark" startIcon={<LineUsdCircleIcon />}>
              {copy.saveAmountBenefit}
            </BodyTextBlock>}
            {inclusions?.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} colour="highlight-primary-dark" isHighlighted />)}
            {tileInclusions?.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} colour="highlight-primary-dark" isHighlighted />)}
            {insuranceAvailableCopy && <BodyTextBlock variant="medium" weight="bold" colour="highlight-primary-dark" startIcon={<LineShieldCheckIcon />}>
              {insuranceAvailableCopy}
            </BodyTextBlock>}
            {bookingProtectionCopy && <BodyTextBlock variant="medium" weight="bold" colour="highlight-primary-dark" startIcon={<LineShieldCheckIcon />}>
              {bookingProtectionCopy}
            </BodyTextBlock>}
            {!hideSpecialLabel && <BodyTextBlock variant="medium" weight="bold" colour="highlight-primary-dark" startIcon={<LineStarIcon />}>
              <FormattedMessage
                id="ITcfmkJPp9ZFeiycATYu"
                defaultMessage="Special offer: No one-off joining fee if you book today (save {joinFee})"
                description="Special offer banner showing waived joining fee savings in LuxPlus+ benefits modal"
                values={{ joinFee: <FormatCurrency format="roundedDollar" value={subscription.offers[0].joinFee} /> }}
              />
            </BodyTextBlock>}
          </Group>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: '8H7ySSEp3OwGf5Jd4RWc',
              defaultMessage: 'You’ll also enjoy these unlimited benefits all year round:',
              description: 'Section heading for unlimited yearly benefits in LuxPlus+ benefits modal',
            })}
          </BodyText>
          <LuxPlusBenefitsList kind="long" benefits={benefitsForList} />
          <TextLink weight="bold" colour="highlight-primary-dark" size="medium" endIcon={<LineInfoCircleIcon colour="neutral-one" />} onClick={onShowCancellationPolicy}>
            {intl.formatMessage({
              id: 'PAF3hAXTodI-AR5rwIi4',
              defaultMessage: 'Cancel renewal anytime',
              description: 'Link text to view cancellation policy in LuxPlus+ benefits modal',
            })}
          </TextLink>
        </Group>
        <LuxPlusSubscriptionTsAndCs onTsAndCsLinkClick={resolve} type={offer?.luxPlus.access === 'memberOnly' ? undefined : 'withPrice'} />
      </Group>}
  </Modal>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  subscription: state.luxPlus.products.subscription,
})

export default connect(mapStateToProps)(LuxPlusBenefitsModal)
